import type { DayPost } from './data';

/**
 * Nobody sees the family's GiviSnaps until they have posted their own.
 *
 * The point of the prompt is that everyone answers it, and seeing everybody
 * else's first made answering optional: the photos were there either way. Now
 * the GiviSnap page shows who has posted, frosted over, and the way in is
 * posting. Kept in the app only -- the rules and the gateway still let any
 * member read any post -- which is all a family needs.
 *
 * Locked until the prompt in hand is answered, which is the same test the
 * camera's GiviSnap tile uses. Before today's prompt fires that is still
 * yesterday's, so somebody who answered it keeps seeing everyone's until then.
 */
export function isLocked({
  settled,
  userLoaded,
  promptOpen,
  answeredHere,
}: {
  settled: boolean;
  userLoaded: boolean;
  promptOpen: boolean;
  answeredHere: boolean;
}): boolean {
  // Not known yet is not locked: frosting everything for the moment the app
  // takes to load would flash at somebody who posted hours ago.
  if (!settled || !userLoaded) return false;
  return promptOpen && !answeredHere;
}

/** Only your own while locked: what the full-screen viewer and the warm-up may have. */
export function unlockedPosts(posts: DayPost[], uid: string, locked: boolean): DayPost[] {
  return locked ? posts.filter((post) => post.data.authorUid === uid) : posts;
}

/*
 * Posted from this phone, for this prompt. The post itself only exists once
 * its upload is through, which on a weak signal is minutes; the person who
 * just pressed Post should not land back on a page still frosted over.
 */
const ANSWERED_KEY = 'givisnap:answeredDay';

export function markAnswered(day: string, storage: Pick<Storage, 'setItem'> = localStorage): void {
  try {
    storage.setItem(ANSWERED_KEY, day);
  } catch {
    // Blocked storage: unlocked when the upload lands instead.
  }
}

export function answeredHere(day: string, storage: Pick<Storage, 'getItem'> = localStorage): boolean {
  try {
    return storage.getItem(ANSWERED_KEY) === day;
  } catch {
    return false;
  }
}
